import { useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'react-query';
import { useSelector } from 'react-redux';

import { Main as Container } from 'Components/Main';
import { Details } from './Details';
import { addPolicy, editPolicy, getPolicyById } from 'services/policies-service';
import { getUsersGroups } from 'services/users-groups-service';
import { useAuthTenant } from 'store/auth';
import { DotsLoader } from 'Components/DotsLoader';
import { Dialog, PageHeader } from 'Components/ui';

/* ── Styled Components ── */

const PageWrap = styled.div`
  padding-top: 24px;
  padding-bottom: 32px;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
`;

const CancelBtn = styled.button`
  height: 44px;
  background: white;
  color: var(--vo-celeste);
  border: 1.5px solid var(--vo-celeste);
  border-radius: 10px;
  font-family: 'Maven Pro', sans-serif;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  padding: 0 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  &:hover { background: var(--vo-capri-50); }
`;

const SaveBtn = styled.button`
  height: 44px;
  background: var(--vo-celeste);
  color: white;
  border: none;
  border-radius: 10px;
  font-family: 'Maven Pro', sans-serif;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  padding: 0 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  &:hover:not(:disabled) { background: var(--vo-cobalto); }
  &:disabled { opacity: .6; cursor: default; }
`;

/* ── Component ── */

export const PoliciesForm = () => {
  const { t } = useTranslation(['policy']);
  const { id } = useParams();
  const selectedTenant = useSelector(useAuthTenant);
  const navigate = useNavigate();

  const { data: usersGroups = [] } = useQuery(['getUsersGroups'], async () => {
    if (selectedTenant.slug === undefined) return [];
    const result = await getUsersGroups(selectedTenant.slug);
    return result?.data?.length > 0 ? result?.data : [];
  });

  const {
    register,
    formState: { errors },
    control,
    getValues,
    setValue,
    handleSubmit,
  } = useForm({
    defaultValues: {
      name: '',
      classification: '',
      userGroups: [],
      description: '',
      companyKey: selectedTenant.slug,
    },
  });

  const formatForm = data => ({
    ...data,
    userGroups: data.userGroups.length
      ? data.userGroups.map(userGroupId => {
          const userGroup = usersGroups.find(g => g._id === userGroupId);
          return userGroup
            ? { _id: userGroup._id, name: userGroup.name, active: userGroup.active, companyKey: userGroup.companyKey }
            : { _id: userGroupId, name: '', active: true, companyKey: selectedTenant.slug };
        })
      : [],
  });

  const mutation = useMutation(
    () => (id ? editPolicy(id, formatForm(getValues())) : addPolicy(formatForm(getValues()))),
    {
      onError: error => {
        Dialog.fire({
          title: t('policy:form.errorTitle'),
          html: error.response?.data?.message || error.message,
          icon: 'error',
        });
      },
      onSuccess: data => {
        navigate(`/policies/show/${id || data.data.id}`);
      },
    },
  );

  const handleComplete = () => mutation.mutate();

  const getPolicy = useCallback(async () => {
    if (id) {
      try {
        const resPolicy = await getPolicyById(id);
        Object.keys(resPolicy.data).forEach(key => {
          if (key === 'userGroups') {
            setValue(key, resPolicy.data[key]?.map(g => g._id) || []);
          } else {
            setValue(key, resPolicy.data[key]);
          }
        });
      } catch (error) {
        Dialog.alert({
          title: t('policy:form.errorTitle'),
          message: error.response?.data?.message ?? error.message,
          icon: 'error',
        });
      }
    }
  }, [id, setValue, t]);

  useEffect(() => { getPolicy(); }, [getPolicy]);

  const pageTitle = id ? t('policy:form.titleEdit') : t('policy:form.titleAdd');

  return (
    <Container>
      <PageWrap>
        {/* Page Header */}
        <PageHeader
          title={pageTitle}
          segments={[
            { label: t('common:breadcrumbs.compliance') },
            { label: t('policy:page.title'), route: '/policies' },
            { label: pageTitle },
          ]}
          actions={
            <HeaderActions>
              <CancelBtn type="button" onClick={() => navigate('/policies')}>
                {t('policy:form.cancel')}
              </CancelBtn>
              <SaveBtn
                type="submit"
                form="policy-form"
                disabled={mutation.isLoading}
              >
                {mutation.isLoading ? <DotsLoader /> : t('policy:form.save')}
              </SaveBtn>
            </HeaderActions>
          }
        />

        {/* Form Card */}
        <Details
          control={control}
          register={register}
          isLoading={mutation.isLoading}
          errors={errors}
          handleComplete={handleComplete}
          handleSubmit={handleSubmit}
          usersGroups={usersGroups}
        />
      </PageWrap>
    </Container>
  );
};
